// src/pages/Results.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { getSearchResultsConfig, searchVehicles } from "../api/vehicleApi";
import { addToCartForCurrentUser } from "../api/cartApi";
import VehicleCard from "../components/search/VehicleCard";
import Filters from "../components/search/Filters";
import SkeletonCard from "../components/search/SkeletonCard";
import Header from "../components/Header";
import { useUser, ROLES } from "../context/UserContext";
import { toast } from "../lib/toast";
import carDetails from "../data/carDetails.json";

/* ------------------------- time helpers ------------------------- */
function to24h(t) {
  if (!t) return undefined;
  if (/^\d{2}:\d{2}$/.test(t)) return t;
  const [hh, mm, period] = t.split(/:| /);
  let h = parseInt(hh, 10);
  if (!Number.isFinite(h)) return undefined;
  if (period?.toLowerCase() === "pm" && h !== 12) h += 12;
  if (period?.toLowerCase() === "am" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${mm}`;
}

function hasRequired(p) {
  const hasPickup = !!p.pickupLocation?.trim();

  const hasPickupDT = !!p.pickupDate && !!(p.pickupTime24 || p.pickupTime);
  const hasDropDT = !!p.releaseDate && !!(p.releaseTime24 || p.releaseTime);

  const hasDurOrDist =
    (typeof p.durationHours === "number" && p.durationHours > 0) ||
    (typeof p.totalDistance === "number" && p.totalDistance > 0);

  return hasPickup && hasPickupDT && hasDropDT && hasDurOrDist;
}

function formatDateShort(dateStr) {
  if (!dateStr) return "N/A";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return "N/A";
  return dt.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const CATALOG_VEHICLE_META_KEY = "wr_catalog_vehicle_meta_v1";
const SCHEDULED_BOOKING_NEW_PRICING_MODE = "DISCOUNT_REDUCES_TOTAL";

function readCatalogVehicleMetaCache() {
  try {
    const raw = window.localStorage.getItem(CATALOG_VEHICLE_META_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeCatalogVehicleMetaCache(cache) {
  try {
    window.localStorage.setItem(CATALOG_VEHICLE_META_KEY, JSON.stringify(cache || {}));
  } catch {
    // ignore storage failures
  }
}

function catalogVehicleCacheKey(source) {
  const reg =
    source?.registrationNumber ||
    source?.vehicleRegistrationNumber ||
    source?.vehicle?.registrationNumber ||
    "";
  return String(reg || "").trim();
}

function toCatalogVehicleMeta(source) {
  const key = catalogVehicleCacheKey(source);
  if (!key) return null;

  return {
    registrationNumber: key,
    vehicleRegistrationNumber: key,
    brand: source?.brand || source?.vehicle?.brand || "",
    model: source?.model || source?.vehicle?.model || "",
    type: source?.type || source?.vehicle?.type || "",
    seatingCapacity:
      source?.seatingCapacity ??
      source?.vehicle?.seatingCapacity ??
      source?.vehicle?.seatCount ??
      source?.vehicle?.seats ??
      null,
    fuelType: source?.fuelType || source?.vehicle?.fuelType || "",
    color: source?.color || source?.vehicle?.color || "",
    yearOfPurchase: source?.yearOfPurchase ?? source?.vehicle?.yearOfPurchase ?? null,
    imageUrl: source?.imageUrl || source?.vehicle?.imageUrl || null,
    modelImage: source?.modelImage || source?.vehicle?.modelImage || null,
  };
}

function saveCatalogVehicleMetaBatch(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return;

  const next = { ...readCatalogVehicleMetaCache() };
  let changed = false;

  rows.forEach((row) => {
    const meta = toCatalogVehicleMeta(row);
    if (!meta) return;
    next[meta.registrationNumber] = {
      ...(next[meta.registrationNumber] || {}),
      ...meta,
    };
    changed = true;
  });

  if (changed) writeCatalogVehicleMetaCache(next);
}

/* -------------------- build O(1) image index ------------------- */
function makeImageIndex(details) {
  const idx = Object.create(null);

  for (const brand of Object.keys(details || {})) {
    const brandNode = details[brand];
    if (!brandNode?.cars) continue;

    idx[brand] = Object.create(null);

    for (const m of brandNode.cars) {
      const colorMap = Object.create(null);

      if (m?.colorImages && typeof m.colorImages === "object") {
        for (const c of Object.keys(m.colorImages)) {
          colorMap[c] = m.colorImages[c];
        }
      } else if (Array.isArray(m?.colors)) {
        for (const c of m.colors) {
          if (c && typeof c === "object" && c.name && c.image) {
            colorMap[c.name] = c.image;
          }
        }
      }

      idx[brand][m.name] = {
        colorMap,
        modelImage: m.image || null,
      };
    }
  }

  return idx;
}

function getImageFromIndex(index, brand, model, color) {
  const b = index?.[brand];
  const m = b?.[model];

  if (!m) {
    return { imageUrl: "", modelImage: null };
  }

  return {
    imageUrl: m.colorMap?.[color] || "",
    modelImage: m.modelImage || null,
  };
}

function parseYearFromPurchase(yearOfPurchase) {
  if (!yearOfPurchase) return null;

  const m = String(yearOfPurchase).match(/(19\d{2}|20\d{2})/);
  if (!m) return null;

  const y = Number(m[1]);
  return Number.isFinite(y) ? y : null;
}

function computeVehicleAgeYears(yearOfPurchase) {
  const y = parseYearFromPurchase(yearOfPurchase);
  if (!y) return null;

  const nowY = new Date().getFullYear();
  return Math.max(0, nowY - y);
}

/* --------------------- row normalization ----------------------- */
function normalizeRow(r) {
  const v = r.vehicle || r;

  const baseFare = Number(r.fare ?? r.baseFare ?? 0);
  const basePrice = Number(r.basePrice ?? 0);
  const fuelCost = Number(r.fuelCost ?? 0);
  const toll = Number(r.displayToll ?? r.display_toll ?? r.toll ?? 0);
  const commission = Number(r.commission ?? 0);
  const apiCharges = Number(r.apiCharges ?? 0);
  const gst = Number(r.gst ?? 0);

  const eligibleDriverCount =
    r?.eligibleDriverCount != null ? Number(r.eligibleDriverCount) : null;

  const eligibleDrivers = Array.isArray(r?.eligibleDrivers)
    ? r.eligibleDrivers
        .map((d) => ({
          id: d?.id ?? null,
          name: d?.name ?? "",
          phoneNumber: d?.phoneNumber ?? "",
        }))
        .filter((d) => d.id != null || d.phoneNumber)
    : null;

  const yearOfPurchase = v?.yearOfPurchase ?? r?.yearOfPurchase ?? null;
  const vehicleAgeYears = computeVehicleAgeYears(yearOfPurchase);

  return {
    id: r.id || v.registrationNumber || r.registrationNumber,

    brand: v.brand,
    model: v.model,
    type: v.type,

    seatingCapacity: v.seatingCapacity,
    fuelType: v.fuelType,
    color: v.color,

    registrationNumber: v.registrationNumber,
    availableCount:
      r?.availableCount != null && Number.isFinite(Number(r.availableCount))
        ? Math.max(0, Number(r.availableCount))
        : 1,
    imageUrl: v?.imageUrl ?? r?.imageUrl ?? null,
    modelImage: v?.modelImage ?? r?.modelImage ?? null,

    yearOfPurchase,
    vehicleAgeYears,

    baseFare,
    basePrice,
    fuelCost,
    toll,
    commission,
    apiCharges,
    gst,

    ratingAverage: r.ratingAverage != null ? Number(r.ratingAverage) : null,
    ratingCount: r.ratingCount != null ? Number(r.ratingCount) : null,

    eligibleDriverCount,
    eligibleDrivers,
  };
}

/* --------------------------- page ------------------------------ */
export default function Results() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const { role, isAuthenticated } = useUser();

  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scheduleBookingDiscountPercent, setScheduleBookingDiscountPercent] = useState(0);

  const [selected, setSelected] = useState({
    brandModel: [],
    fuelType: [],
    seatingCapacity: [],
    color: [],
    fareRange: [],
    rating: [],
  });

  const [sort, setSort] = useState("fare-asc");
  const [showFilters, setShowFilters] = useState(false);

  const imageIndex = useMemo(() => makeImageIndex(carDetails), []);

const payload = useMemo(() => {
  const params = new URLSearchParams(search);
  const p = Object.fromEntries(params.entries());
  const destinations = params.getAll("destinations");
  const stopsResolved = params.getAll("stopsResolved");
  const stopCoordinates = params.getAll("stopCoordinates");

  const readListCSV = (val) =>
    !val ? [] : String(val).split(",").map((s) => s.trim()).filter(Boolean);

  const num = (v) => (v === undefined || v === "" ? undefined : Number(v));

  const pickupTime24 = p.pickupTime24 || to24h(p.pickupTime);
  const releaseTime24 = p.releaseTime24 || to24h(p.releaseTime);

  const carTypes =
    p.types ? readListCSV(p.types)
    : p.carType ? readListCSV(p.carType)
    : p.type ? readListCSV(p.type)
    : [];
  const categories =
    p.categories ? readListCSV(p.categories)
    : p.category === "all" ? ["normal", "premium"]
    : p.category ? readListCSV(p.category)
    : ["normal", "premium"];

  return {
    pickupLocation: p.pickupLocation ?? "",
    pickupDate: p.pickupDate || undefined,
    pickupTime24,
    pickupTime: p.pickupTime,
    destinations,
    stopsResolved,
    stopCoordinates,
    pickupResolved: p.pickupResolved || undefined,
    finalDestination: p.finalDestination || undefined,
    finalResolved: p.finalResolved || undefined,
    releaseDate: p.releaseDate || undefined,
    releaseTime24,
    releaseTime: p.releaseTime,
    radius: p.radius ? Number(p.radius) : 20,
    totalDistance: num(p.totalDistance),
    durationHours: num(p.durationHours),
    minDropOffsetMin: num(p.minDropOffsetMin),
    pickupLat: num(p.pickupLat),
    pickupLng: num(p.pickupLng),
    dropLat: num(p.dropLat),
    dropLng: num(p.dropLng),
    roundTrip: p.roundTrip === "true",

    carTypes,
    carType: carTypes[0] || undefined,

    category: p.category || undefined,
    categories,
  };
}, [search]);

  const ready = hasRequired(payload);

  const canAddToCart = !!isAuthenticated && role === ROLES.USER;
  const isOwnerRole = false;
  const isDriverRole = false;
  const routeSummary = useMemo(() => {
    const stopCount = Array.isArray(payload.destinations)
      ? payload.destinations.filter(Boolean).length
      : 0;
    if (!ready) return "Trip details needed";
    return `${stopCount + 2} route point${stopCount + 2 === 1 ? "" : "s"}`;
  }, [payload.destinations, ready]);
  const scheduleSummary = useMemo(() => {
    if (!ready) return "Add pickup and drop timing";
    return `${formatDateShort(payload.pickupDate)} • ${
      payload.pickupTime24 || payload.pickupTime || "N/A"
    }`;
  }, [payload.pickupDate, payload.pickupTime, payload.pickupTime24, ready]);
  const releaseSummary = useMemo(() => {
    if (!ready) return "Release timing pending";
    return `${formatDateShort(payload.releaseDate)} • ${
      payload.releaseTime24 || payload.releaseTime || "N/A"
    }`;
  }, [payload.releaseDate, payload.releaseTime, payload.releaseTime24, ready]);

  const handleEditTrip = () => {
    navigate({ pathname: "/discover", search });
  };

  const topRef = useRef(null);
  const prevBusyRef = useRef(false);

  useEffect(() => {
    if (prevBusyRef.current && !busy) {
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: "auto", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    }
    prevBusyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    if (ready) {
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: "auto", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  }, [ready, payload]);

  useEffect(() => {
    let alive = true;

    getSearchResultsConfig()
      .then((config) => {
        if (!alive) return;
        setScheduleBookingDiscountPercent(Number(config?.scheduleBookingDiscountPercent) || 0);
      })
      .catch(() => {
        if (alive) setScheduleBookingDiscountPercent(0);
      });

    return () => {
      alive = false;
    };
  }, []);

  /* ----------------------- fetch & compose ---------------------- */
  useEffect(() => {
    let mounted = true;

    if (!ready) {
      setItems([]);
      return;
    }

    setBusy(true);

    (async () => {
      try {
        console.log("[WEB][Results] fetching vehicles", payload);
        const rows = await searchVehicles(payload);
        if (!mounted) return;
        console.log("[WEB][Results] backend rows received", {
          count: rows?.length || 0,
          sample: rows?.[0] || null,
        });

        const normalized = (rows || []).map((r, i) => {
          const n = normalizeRow(r);
          const img = getImageFromIndex(imageIndex, n.brand, n.model, n.color);

          return {
            ...n,
            id: n.id || n.registrationNumber || `row-${i}`,
            imageUrl: n.imageUrl || img.imageUrl,
            modelImage: n.modelImage || img.modelImage,
          };
        });

        saveCatalogVehicleMetaBatch(normalized);
        setItems(normalized);
      } catch (e) {
        console.error("Vehicle fetch failed:", e);
        if (mounted) {
          setItems([]);
        }
      } finally {
        if (mounted) setBusy(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [ready, payload, imageIndex]);

  /* -------------------- derive filter options ------------------- */
  const filterOptions = useMemo(() => {
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

    const brandModel = uniq(items.map((v) => `${v.brand} ${v.model}`.trim()));
    const fuelType = uniq(items.map((v) => v.fuelType)).sort();
    const seatingCapacity = uniq(items.map((v) => v.seatingCapacity)).sort((a, b) => a - b);
    const color = uniq(items.map((v) => v.color)).sort();
    const fareRanges = ["< 1000", "1000 - 5000", "5000 - 10000", "> 10000"];
    const ratingThresholds = [4, 3, 1];

    return { brandModel, fuelType, seatingCapacity, color, fareRanges, ratingThresholds };
  }, [items]);

  /* -------------------- apply filters + sort -------------------- */
  const filtered = useMemo(() => {
    const fareInBucket = (fare, buckets) => {
      if (!buckets?.length) return true;

      return buckets.some((b) => {
        if (b === "< 1000") return fare < 1000;
        if (b === "1000 - 5000") return fare >= 1000 && fare <= 5000;
        if (b === "5000 - 10000") return fare > 5000 && fare <= 10000;
        if (b === "> 10000") return fare > 10000;
        return true;
      });
    };

    let out = items.filter((v) => {
      const bm = `${v.brand} ${v.model}`.trim();

      const passBrand = !selected.brandModel.length || selected.brandModel.includes(bm);
      const passFuel = !selected.fuelType.length || selected.fuelType.includes(v.fuelType);
      const passSeats =
        !selected.seatingCapacity.length || selected.seatingCapacity.includes(v.seatingCapacity);
      const passColor = !selected.color.length || selected.color.includes(v.color);

      const totalFare = Number(v.baseFare ?? v.fare ?? 0);
      const passFare = fareInBucket(totalFare, selected.fareRange);

      const avg = typeof v.ratingAverage === "number" ? v.ratingAverage : null;
      const passRating =
        selected.rating.length === 0
          ? true
          : avg == null
          ? true
          : avg >= Math.min(...selected.rating);

      return passBrand && passFuel && passSeats && passColor && passFare && passRating;
    });

    const aFare = (x) => Number(x.baseFare ?? x.fare ?? 0);

    if (sort === "fare-asc") out.sort((a, b) => aFare(a) - aFare(b));
    if (sort === "fare-desc") out.sort((a, b) => aFare(b) - aFare(a));

    return out;
  }, [items, selected, sort]);

  useEffect(() => {
    if (busy) return;
    console.log("[WEB][Results] visible rows after filters", {
      apiRows: items.length,
      shownRows: filtered.length,
      selectedFilters: selected,
      sort,
    });
  }, [busy, items.length, filtered.length, selected, sort]);

  /* ----------------------- add to cart -------------------------- */
  const handleAddToCart = async (row, quantity = 1) => {
    if (role !== ROLES.USER) {
      toast("Please sign in with a user account to add cars to your cart.");
      navigate("/signin-required", {
        state: { from: `/results${search}` },
        replace: false,
      });
      return;
    }

    if (!isAuthenticated) {
      toast("Please sign in to add cars to your cart.");
      navigate("/signin-required", {
        state: { from: `/results${search}` },
        replace: false,
      });
      return;
    }

    const dto = {
      vehicleRegistrationNumber: row.registrationNumber,
      catalogBrand: row.brand || "",
      catalogModel: row.model || "",
      catalogType: row.type || "",
      catalogSeatingCapacity:
        Number.isFinite(Number(row.seatingCapacity)) && Number(row.seatingCapacity) > 0
          ? String(Math.trunc(Number(row.seatingCapacity)))
          : "",
      catalogFuelType: row.fuelType || "",
      catalogColor: row.color || "",
      catalogYearOfPurchase: row.yearOfPurchase ? String(row.yearOfPurchase) : "",
      imageUrl: row.imageUrl || null,
      modelImage: row.modelImage || null,
      quantity: Math.max(1, Math.trunc(Number(quantity) || 1)),
      availableCount:
        Number.isFinite(Number(row.availableCount)) && Number(row.availableCount) > 0
          ? Math.trunc(Number(row.availableCount))
          : 1,
      basePrice: Number(row.basePrice || 0),
      fuelCost: Number(row.fuelCost || 0),
      toll: Number(row.toll || 0),
      displayToll: Number((row.displayToll ?? row.display_toll ?? row.toll) || 0),
      commission: Number(row.commission || 0),
      apiCharges: Number(row.apiCharges || 0),
      gst: Number(row.gst || 0),
      fare: Number(row.fare || row.baseFare || 0),

      pickupLocation: payload.pickupLocation,
      pickupDate: payload.pickupDate,
      pickupTime: payload.pickupTime24 || payload.pickupTime,
      destinations: payload.destinations || [],
      destination: payload.finalDestination,
      dropDate: payload.releaseDate,
      dropTime: payload.releaseTime24 || payload.releaseTime,
      radius: Number(payload.radius || 0),
      travelDistance: Number(payload.totalDistance || 0),
      durationHours: Number(payload.durationHours || 0),
      scheduledBookingPricingMode: SCHEDULED_BOOKING_NEW_PRICING_MODE,
    };

    saveCatalogVehicleMetaBatch([row, dto]);
    console.log("[WEB][Results] add to cart payload", dto);

    try {
      await addToCartForCurrentUser(dto);
      setItems((prev) =>
        prev.map((v) =>
          v.registrationNumber !== row.registrationNumber
            ? v
            : {
                ...v,
                availableCount: Math.max(
                  0,
                  Number(v.availableCount || 0) - Math.max(1, Math.trunc(Number(quantity) || 1))
                ),
              }
        ).filter((v) => Number(v.availableCount ?? 1) > 0)
      );
    } catch (e) {
      console.error("Add to cart failed", e);

      if (e?.code === "AUTH_REQUIRED") {
        toast("Session not found. Please sign in again.");
        navigate("/signin-required", {
          state: { from: `/results${search}` },
          replace: false,
        });
        return;
      }

      toast("Something went wrong while adding the car to your cart.");
    }
  };

  /* --------------------------- UI ------------------------------- */
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="bg-white">
        <div className="mx-auto max-w-[1560px] px-4 py-6 sm:px-6 sm:py-8 xl:px-10 2xl:max-w-[1760px] 2xl:px-12">
          <div className="mb-4 scroll-mt-24 sm:mb-6" ref={topRef}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold">Available vehicles</h1>
                <p className="text-sm text-slate-500 sm:text-base sm:text-slate-600">
                  {!ready
                    ? "Add required trip details to search."
                    : busy
                    ? "Fetching options…"
                    : `Showing ${filtered.length} result${filtered.length !== 1 ? "s" : ""}`}
                </p>
              </div>

              {ready && (
                <button
                  type="button"
                  onClick={handleEditTrip}
                  className="hidden items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 active:bg-slate-100 sm:inline-flex"
                >
                  Edit trip
                </button>
              )}
            </div>

            <div
              className="mt-3 hidden h-[3px] w-32 rounded-full sm:block"
              style={{ background: "var(--brand-gradient)" }}
            />
          </div>

          {isOwnerRole && (
            <div className="mb-6 rounded-2xl border border-pink-200 bg-pink-50 px-4 py-3 text-sm text-pink-900">
              <span className="mr-1 font-semibold">Info:</span>
              You’re signed in as a vehicle owner. To book cars and add them to your cart, please
              sign in with a user account.
            </div>
          )}

          {isDriverRole && (
            <div className="mb-6 rounded-2xl border border-pink-200 bg-pink-50 px-4 py-3 text-sm text-pink-900">
              <span className="mr-1 font-semibold">Info:</span>
              You’re signed in as a driver. To book cars and add them to your cart, please sign in
              with a user account.
            </div>
          )}

          {!ready ? (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6">
              Please provide a pickup location and either both pickup &amp; drop date/time or a
              duration/distance.{" "}
              <Link to="/discover" className="underline">
                Go back to trip setup
              </Link>
              .
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_minmax(0,1fr)] xl:gap-8">
              <aside className="hidden xl:block">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <Filters
                    options={filterOptions}
                    selected={selected}
                    onChange={setSelected}
                    sort={sort}
                    setSort={setSort}
                  />
                </div>
              </aside>

              <section className="space-y-3 sm:space-y-4">
                <div className="hidden items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                      Vehicle matches
                    </div>
                    <div className="text-sm text-slate-700">
                      Compare fare, vehicle details, and add the right cars to your cart.
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {!busy ? `${filtered.length} shown` : "Loading"}
                  </div>
                </div>
                <div className="xl:hidden">
                  <div className="rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur">
                    <div className="hidden flex-wrap items-center justify-between gap-2 sm:flex">
                      <span className="text-sm font-medium text-slate-700">Filters &amp; Sort</span>
                      <button
                        onClick={() => setShowFilters((s) => !s)}
                        className="rounded-lg border px-3 py-1.5 text-sm"
                        type="button"
                      >
                        {showFilters ? "Hide" : "Show"}
                      </button>
                    </div>

                    <div className="flex items-center gap-2 sm:hidden">
                      {ready ? (
                        <button
                          type="button"
                          onClick={handleEditTrip}
                          className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm"
                        >
                          Edit trip
                        </button>
                      ) : null}
                      <button
                        onClick={() => setShowFilters((s) => !s)}
                        className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm"
                        type="button"
                      >
                        Filters
                      </button>
                    </div>

                    {showFilters && (
                      <div className="mt-2">
                        <Filters
                          options={filterOptions}
                          selected={selected}
                          onChange={setSelected}
                          sort={sort}
                          setSort={setSort}
                          layout="bar"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {busy
                    ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} variant="row" />)
                    : filtered.map((v) => {
                        const cardCanAdd = canAddToCart;
                        const disabledHint =
                          !cardCanAdd && role !== ROLES.USER
                            ? "Sign in as USER to add to cart."
                            : !cardCanAdd
                            ? "Please sign in to add to cart."
                            : undefined;

                        return (
                          <VehicleCard
                            key={v.id}
                            brand={v.brand}
                            model={v.model}
                            type={v.type}
                            seatingCapacity={v.seatingCapacity}
                            color={v.color}
                            fare={v.fare}
                            baseFare={v.baseFare}
                            availableCount={v.availableCount}
                            imageUrl={v.imageUrl}
                            modelImage={v.modelImage}
                            toll={v.toll}
                            commission={v.commission}
                            apiCharges={v.apiCharges}
                            gst={v.gst}
                            discountPercent={scheduleBookingDiscountPercent}
                            canAddToCart={cardCanAdd}
                            disabledHint={disabledHint}
                            onAddToCart={(quantity) => handleAddToCart(v, quantity)}
                          />
                        );
                      })}

                  {!busy && filtered.length === 0 && (
                    <div className="rounded-2xl border border-slate-200 p-8 text-center text-slate-600">
                      {items.length === 0
                        ? "No vehicles were returned for this trip yet. Please review the trip details and try again."
                        : "No vehicles match your filters. Try widening your search."}
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
          <img
            src="/Wayroot (1600 x 500 px).svg"
            alt="Wayroot"
            className="h-8 w-auto"
          />
        </div>
      </footer>
    </div>
  ); 
}
